import { inject, Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
    AppNotificationService,
    AppNotificationType,
    ArrayModel,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigInstanceToken,
    ConfigInterface,
    EventService,
    LoadingScreenComponent,
    LoadingScreenModel,
    MaterialModel,
    MenuEntryModel,
    MenuSectionModel,
    ModalClass,
    selectIsLocationTrackerEnabled,
    SideMenuInterface,
    SideMenuService,
    SynchronizationService,
    TimesheetAndExpensesInstanceToken,
    TimesheetAndExpensesInterface,
    TimesheetService,
    UserMasterInstanceToken,
    UserMasterInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, take } from 'rxjs/operators';
import { CseSalesTidInputComponent } from './material/cse-sales-tid-input/cse-sales-tid-input.component';
import { MaterialAvailabilityComponent } from './material/material-availability/material-availability.component';
import { MaterialHistoryComponent } from './material/material-history/material-history.component';
import { MaterialInventoryComponent } from './material/material-inventory/material-inventory.component';
import { MeasurementToolsComponent } from './shared/components/measurement-tools/measurement-tools.component';
import { RequestNotificationComponent } from './shared/components/request-notification/request-notification.component';
import { MaterialStockService } from './material/services/material-stock-service';
import {
    faBullhorn,
    faChartLine,
    faClock,
    faFilePdf,
    faLocationArrow,
    faMoneyCheckAlt,
    faPlay,
    faTh,
    faToolbox,
    faTruck,
    faTruckLoading,
    faUndo,
    faWarehouse,
} from '@fortawesome/free-solid-svg-icons';
import { MaterialReturnP58Component } from './material/material-return-p58/material-return-p58.component';
import MaterialReturnP58Model from './material/material-return-p58/material-return-p58.model';
import { StartWorkComponent } from './timesheet/components/start-work/start-work.component';
import StartWorkModel from './timesheet/components/start-work/start-work.model';
import { DocBoxTabbedModalComponent } from './docbox-tabbed-modal/docbox-tabbed-modal.component';
import { environment } from '../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class PaceSideMenuService implements SideMenuInterface {
    public isInitialized = false;
    private readonly store = inject(Store);
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
        private readonly sideMenuService: SideMenuService,
        private readonly translate: TranslateService,
        private readonly router: Router,
        private readonly synchronizationService: SynchronizationService,
        private readonly appNotificationService: AppNotificationService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly modalService: BsModalService,
        private readonly materialStockService: MaterialStockService,
        @Inject(TimesheetAndExpensesInstanceToken)
        private readonly timesheetAndExpensesService: TimesheetAndExpensesInterface,
        private readonly eventService: EventService,
        private readonly timesheetService: TimesheetService,
    ) {}

    public init(): Observable<void> {
        // Define the Menu Sections
        const workingTimeSection = new MenuSectionModel(
            <string>this.translate.instant('SIDEMENU_SECTION_WORKING_TIME_LABEL'),
        );

        const materialSection = new MenuSectionModel(<string>this.translate.instant('SIDEMENU_SECTION_MATERIAL_LABEL'));

        const miscellaneousSection = new MenuSectionModel(
            <string>this.translate.instant('SIDEMENU_SECTION_MISCELLANEOUS_LABEL'),
        );

        const createNotificationEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_CREATE_NOTIFICATION_LABEL'),
            () => this.openCreateNotification(),
            faBullhorn,
        );
        const materialAvailabilityEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_MATERIAL_AVAILABILITY_LABEL'),
            () => this.openMaterialAvailability(),
            faTruck,
        );
        const openBootStockEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_BOOT_STOCK_LABEL'),
            () => this.openBootStock(),
            faTruckLoading,
        );
        const materialHistoryEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_MATERIAL_HISTORY_LABEL'),
            () => this.openMaterialHistory(),
            faChartLine,
        );
        const materialReturnEntry = new MenuEntryModel(
            <string>this.translate.instant('FREQUENT_ACTIVITIES_BUTTON_RETURN_MATERIAL_LABEL'),
            () => this.openMaterialReturn(),
            faUndo,
        );

        const cseSaledTidInputEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_KV_CSE_SALES_LABEL'),
            () => this.openCseSalesTidInput(),
            faMoneyCheckAlt,
        );

        const materialInventoryEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_MATERIAL_INVENTORY_LABEL'),
            () => this.openMaterialInventory(),
            faWarehouse,
        );

        const startWorkEntry = new MenuEntryModel(
            <string>this.translate.instant('FREQUENT_ACTIVITIES_BUTTON_START_WORK_LABEL'),
            () => this.openStartWork(),
            faPlay,
        );

        const timeSheetEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_TIMESHEET_LABEL'),
            () => this.openTimesheet(),
            faClock,
        );

        const measurementToolsEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_MEASUREMENT_TOOL_LABEL'),
            () => this.openMeasurementTools(),
            faToolbox,
        );

        if (this.configService.getEnableJobOnFly()) {
            miscellaneousSection.entries.push(createNotificationEntry);
        }

        // The location tracker only exists on the NgRx data layer. It starts hidden because the app config
        // that enables it loads asynchronously, after the side menu is built.
        const locationTrackerEntry = new MenuEntryModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_LOCATION_TRACKER_LABEL'),
            () => this.openLocationTracker(),
            faLocationArrow,
            false,
        );
        if (environment.enableNgRx) {
            miscellaneousSection.entries.push(locationTrackerEntry);
        }

        if (this.userMasterService.isPaceOnlineUser()) {
            // Entries for PACEOnline
            materialSection.entries.push(
                materialAvailabilityEntry,
                materialHistoryEntry,
                this.clientManager.isNgmP58() ? openBootStockEntry : materialInventoryEntry,
            );
        } else if (this.clientManager.isNgmP58()) {
            // Entries for P58
            materialSection.entries.push(materialAvailabilityEntry, materialHistoryEntry, openBootStockEntry);
            if (this.configService.getDisplayMaterialQuote()) {
                miscellaneousSection.entries.push(cseSaledTidInputEntry);
            }
            if (this.timesheetService.isTimesheetFeatureEnabled()) {
                workingTimeSection.entries.push(startWorkEntry);
                workingTimeSection.entries.push(timeSheetEntry);
            }
        }
        if (this.configService.getShowMeasurementToolsDetails()) {
            miscellaneousSection.entries.push(measurementToolsEntry);
        }

        if (this.configService.getDisplaySideMenuDocBoxFlag() && !this.userMasterService.isPaceOnlineUser()) {
            const docBox = new MenuEntryModel(
                <string>this.translate.instant('SIDEMENU_BUTTON_DOCBOX_LABEL'),
                () => this.openDocBoxModal(),
                faFilePdf,
            );
            miscellaneousSection.entries.push(docBox);
            this.handleSideMenuDocboxButton();
        }

        materialSection.entries.push(materialReturnEntry);

        if (this.configService.getEnableSmartlocker()) {
            const smartLockerEntry = new MenuEntryModel(
                <string>this.translate.instant('SIDEMENU_BUTTON_SMARTLOCKER_SORTING_LABEL'),
                () => this.openSmartlocker(),
                faTh,
            );
            materialSection.entries.push(smartLockerEntry);
        }

        // Add sections to the side menu, but only if it contains entries
        if (workingTimeSection.entries.length > 0) {
            this.sideMenuService.addSection(workingTimeSection);
        }

        this.sideMenuService.addSection(materialSection);
        this.sideMenuService.addSection(miscellaneousSection);

        if (this.timesheetService.isTimesheetFeatureEnabled()) {
            this.timesheetAndExpensesService.getStartOfWork$().subscribe((startOfWork) => {
                const isSoWValid = startOfWork.isValid();
                this.toggleStartWorkEntry(isSoWValid);
            });
        }
        if (environment.enableNgRx) {
            this.handleSideMenuLocationTrackerButton(miscellaneousSection.title, locationTrackerEntry.title);
        }
        this.isInitialized = true;
        return of(undefined);
    }

    private toggleStartWorkEntry(isSoWSet: boolean): void {
        if (isSoWSet) {
            this.disableStartWorkEntry();
        } else {
            this.enableStartWorkEntry();
        }
    }

    private enableStartWorkEntry(): void {
        this.sideMenuService.enableEntry(
            <string>this.translate.instant('SIDEMENU_SECTION_WORKING_TIME_LABEL'),
            <string>this.translate.instant('FREQUENT_ACTIVITIES_BUTTON_START_WORK_LABEL'),
        );
    }

    private disableStartWorkEntry(): void {
        this.sideMenuService.disableEntry(
            <string>this.translate.instant('SIDEMENU_SECTION_WORKING_TIME_LABEL'),
            <string>this.translate.instant('FREQUENT_ACTIVITIES_BUTTON_START_WORK_LABEL'),
        );
    }

    private openCseSalesTidInput(): void {
        this.log.debug('openCseSalesTidInput');
        this.modalService.show(CseSalesTidInputComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            ariaLabelledBy: 'cse-sales-tid-modal-title',
        });
    }

    private openTimesheet(): void {
        this.router.navigate([ConfigConstants.routeUrlTimesheet]).catch((err) => {
            this.log.error(`Could not open timesheet from side menu:`, err);
        });
    }

    private openSmartlocker(): void {
        this.router.navigate([ConfigConstants.routeUrlSmartlocker]).catch((err) => {
            this.log.error(`Could not open Smartlocker sorting from side menu:`, err);
        });
    }

    private openLocationTracker(): void {
        this.router.navigate([ConfigConstants.routeUrlLocationTracker]).catch((err) => {
            this.log.error(`Could not open location tracker from side menu:`, err);
        });
    }

    private reloadOfflineData(): void {
        const loadingScreenModel = new LoadingScreenModel(
            <string>this.translate.instant('RELOAD_OFFLINE_DATA_LOADING_MESSAGE'),
        );
        const modalInstance = this.modalService.show(LoadingScreenComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_SMALL_NO_FULLSCREEN,
            initialState: {
                model: loadingScreenModel,
            },
            ariaLabelledBy: 'loading-screen-modal-title',
        });

        loadingScreenModel.loadingScreenDisplayed$
            .pipe(
                take(1),
                mergeMap(() => {
                    this.log.debug('reloadOfflineData -> receiveEverything');
                    return this.synchronizationService.receiveEverything();
                }),
                catchError(() => {
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.WARN,
                        <string>this.translate.instant('APP_SYNC_FAILED_TITLE'),
                        <string>this.translate.instant('APP_SYNC_FAILED_SYNCHRONIZATION'),
                    );
                    return of(undefined);
                }),
            )
            .subscribe(() => {
                modalInstance.hide();
            });
    }

    private openCreateNotification(): void {
        this.modalService.show(RequestNotificationComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            ariaLabelledBy: 'request-notification-modal-title',
        });
    }

    private openMaterialAvailability(): void {
        this.modalService.show(MaterialAvailabilityComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            ariaLabelledBy: 'material-availability-modal-title',
        });
    }

    private openMaterialHistory(): void {
        this.modalService.show(MaterialHistoryComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            ariaLabelledBy: 'material-history-modal-title',
        });
    }

    private openDocBoxModal(): void {
        this.modalService.show(DocBoxTabbedModalComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            ariaLabelledBy: 'docbox-tabbed-modal-title',
        });
    }

    private openMaterialInventory(): void {
        this.modalService.show(MaterialInventoryComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            ariaLabelledBy: 'material-inventory-modal-title',
        });
    }

    private openMeasurementTools(): void {
        this.modalService.show(MeasurementToolsComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            ariaLabelledBy: 'measurement-tools-modal-title',
        });
    }

    private openBootStock(): void {
        const loadingScreenModel = new LoadingScreenModel(
            <string>this.translate.instant('SIDEMENU_BUTTON_BOOT_STOCK_LOADING_MESSAGE'),
        );
        const loadingScreenInstance = this.modalService.show(LoadingScreenComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_SMALL_NO_FULLSCREEN,
            initialState: {
                model: loadingScreenModel,
            },
            ariaLabelledBy: 'loading-screen-modal-title',
        });

        loadingScreenModel.loadingScreenDisplayed$
            .pipe(
                take(1),
                mergeMap(() =>
                    this.materialStockService.triggerBootStock(
                        undefined,
                        new ArrayModel<MaterialModel>('MaterialSet', []),
                        loadingScreenInstance,
                    ),
                ),
            )
            .subscribe();
    }

    public openMaterialReturn(): void {
        this.modalService.show(MaterialReturnP58Component, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new MaterialReturnP58Model(),
            },
            ariaLabelledBy: 'material-return-p58-modal-title',
        });
    }

    public openStartWork(): void {
        this.timesheetAndExpensesService
            .selectSubmittedTimesheet$()
            .pipe(
                take(1),
                map((timesheet) => timesheet.getEndOfWork()),
                mergeMap((previousEndOfWork) => {
                    const modalInstance = this.modalService.show(StartWorkComponent, {
                        backdrop: 'static',
                        class: ModalClass.MODAL_LARGE,
                        initialState: {
                            model: new StartWorkModel(previousEndOfWork),
                        },
                        ariaLabelledBy: 'start-work-modal-title',
                    });

                    return modalInstance.content?.startWorkAdded ?? of(false);
                }),
            )
            .subscribe((status: boolean) => {
                this.eventService.triggerStartWorkUpdated(status);
                this.disableStartWorkEntry();
            });
    }

    private handleSideMenuLocationTrackerButton(sectionTitle: string, entryTitle: string): void {
        this.store.select(selectIsLocationTrackerEnabled).subscribe((isEnabled) => {
            if (isEnabled) {
                this.sideMenuService.displayEntry(sectionTitle, entryTitle);
            } else {
                this.sideMenuService.hideEntry(sectionTitle, entryTitle);
            }
        });
    }

    private handleSideMenuDocboxButton(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map((event: NavigationEnd) => event.urlAfterRedirects),
                distinctUntilChanged(),
            )
            .subscribe((url) => {
                const isTargetUrl = url.includes(ConfigConstants.DOCBOX_URL);
                return isTargetUrl
                    ? this.sideMenuService.disableEntry(
                          ConfigConstants.SectionTitle.MISCELLANEOUS,
                          ConfigConstants.EntryTitle.DOCBOX,
                      )
                    : this.sideMenuService.enableEntry(
                          ConfigConstants.SectionTitle.MISCELLANEOUS,
                          ConfigConstants.EntryTitle.DOCBOX,
                      );
            });
    }
}
